import {
    Chip,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { iconSize } from 'assets/theme/iconSize';
import { ITableProps } from '@types_main/itable.props';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { convertDateWithHour } from '@functions/convert-date-with-hour';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';

export function UserTable({ data, editAction }: ITableProps<UserResponse>) {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    return (
        <Grid>
            <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="24%">{t('general.nome')}</TableCell>
                            <TableCell width="24%">{t('usuarios.email')}</TableCell>
                            <TableCell width="16%">{t('usuarios.perfil')}</TableCell>
                            <TableCell width="12%">{t('usuarios.situacao')}</TableCell>
                            <TableCell width="14%">{t('usuarios.ultimoAcesso')}</TableCell>
                            <TableCell width="10%" align="center">
                                {t('general.acoes')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            data.data.records.length > 0 ? (
                                data.data.records.map((item) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.email}</TableCell>
                                        <TableCell>
                                            {/* Sem perfil não vê tela nenhuma: precisa saltar aos olhos, não virar célula vazia. */}
                                            {item.profileName ?? (
                                                <Chip
                                                    size="small"
                                                    label={t('usuarios.semPerfil')}
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ '& .MuiChip-label': { color: cc.onTint } }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={item.active
                                                    ? t('usuarios.ativo')
                                                    : t('usuarios.inativo')}
                                                color={item.active ? 'success' : 'default'}
                                                variant={item.active ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* "Nunca entrou" e "entrou há muito" são coisas diferentes: um traço não as distingue. */}
                                            {item.lastLoginAt
                                                ? convertDateWithHour(item.lastLoginAt)
                                                : t('usuarios.nuncaEntrou')}
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Tooltip title={t('general.editar')}>
                                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(item.id)}>
                                                        <EditIcon
                                                            style={iconSize}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={6} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, index) => index).map((index) => (
                                <TableRow hover key={index}>
                                    {Array.from({ length: 6 }, (_, column) => column).map((column) => (
                                        <TableCell key={column}>
                                            <Skeleton height={30} variant="rectangular" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
